/**
 * Phone numbers as the till shows and reads them. The server normalises the same way, so a number
 * typed with +32, read from the caller-ID device or given by a VoIP exchange is one customer.
 */

/** Digits only; "+32 472 18 44 90" and "0032 472…" become "0472184490" (national form). */
export function phoneDigits(value: string): string {
  const digits = value.replace(/\D/g, "");
  if (digits.startsWith("0032")) return `0${digits.slice(4)}`;
  if (digits.startsWith("32") && (digits.length === 10 || digits.length === 11)) return `0${digits.slice(2)}`;
  return digits;
}

/** At least 9 digits: enough to know or save a customer (prototype). */
export const isFullPhone = (value: string) => phoneDigits(value).length >= 9;

/** Belgian zones with a code of two digits (Brussels, Antwerp, Liège, Ghent); the others have three. */
const SHORT_ZONES = new Set(["02", "03", "04", "09"]);

/**
 * A Belgian number written the way it is read (Furkan, 06/10/2026): "+32468344444" → "+32 468 34 44 44",
 * "0468344444" → "0468 34 44 44", "037112233" → "03 711 22 33", "053123456" → "053 12 34 56". A number
 * typed with +32 or 0032 keeps the +32; anything that is no complete Belgian number stays as typed.
 */
export function typedPhone(text: string): string {
  const typed = text.trim();
  const digits = typed.replace(/\D/g, "");
  const international = /^(\+|00)\s*32/.test(typed.replace(/[^\d+]/g, ""));
  const national = international ? `0${digits.replace(/^0032|^32/, "").replace(/^0/, "")}` : digits;
  if (!/^0[1-9]\d{7,8}$/.test(national)) return typed;
  const rest = national.slice(1);
  let groups: string[];
  if (national.length === 10) {
    // a mobile number: 0468 34 44 44
    groups = [rest.slice(0, 3), rest.slice(3, 5), rest.slice(5, 7), rest.slice(7)];
  } else if (SHORT_ZONES.has(national.slice(0, 2))) {
    groups = [rest.slice(0, 1), rest.slice(1, 4), rest.slice(4, 6), rest.slice(6)];
  } else {
    groups = [rest.slice(0, 2), rest.slice(2, 4), rest.slice(4, 6), rest.slice(6)];
  }
  return international ? `+32 ${groups.join(" ")}` : `0${groups.join(" ")}`;
}

/**
 * The digits of a number as it is shown: "0472184490" → "0472 18 44 90", "052123456" → "052 12 34 56",
 * "037112233" → "03 711 22 33" (prototype fmtPhone, with the zones of two digits written as they are read).
 */
export function formatPhone(digits: string): string {
  return typedPhone(digits);
}

/** For a field: the number rewritten when it is left, or null when it already reads right. */
export function phoneOnLeave(value: string): string | null {
  const written = typedPhone(value);
  return written === value.trim() ? null : written;
}
